import { types as loginTypes } from '../login';
import { types as tableTypes } from '../table';
import { types as billTypes } from '../bills';
import { types as rideTypes } from '../rides';
import { types as bookingTypes } from '../bookings';
import { types as reservationTypes } from '../reservations';
import { types as vehicleTypes } from '../vehicles';
import { types as snappayTypes } from '../snappay';
import { types as riderTypes } from '../riders';
import { types as pricingTypes } from '../pricing';
import { types as companyTypes } from '../companies';
import { types as partnerTypes } from '../partners';
import { types as partnerBillTypes } from '../partner-bills';
import { types as driverTypes } from '../drivers';
import { types as documentTypes } from '../documents';
import { types as ticketingTypes } from '../ticketing';
import { types as prebookTypes } from '../prebook';
import { types as viaDialogTypes } from '../viaDialog';
import { types as adminTypes } from '../admin';
import { types as marketingTypes } from '../marketing';
import { types as towerTypes } from '../towers';
import { types as rankTypes } from '../rank';
import { types as driverCommunicationTypes } from '../driver-communication';
import { types as genericTypes } from '../generic';
import { types as ridesImportTypes } from '../rides-import';

export const name: string = 'errorHandler';

type ErrorLOL = any;

interface ErrorFromServerPayload {
    error?: ErrorLOL;
    error_description?: string;
    localized_error?: string;
    details: ErrorLOL;
    description?: string;
    detail?: string;
    message?: string;
}

interface FilterProp {
    key: string;
    value: string;
}

export interface ErrorPayload extends ErrorFromServerPayload {
    errorType: string;
}

export const types = {
    DISPATCH_ERROR: name + '/DISPATCH_ERROR',
    CLEAR_ERROR: name + '/CLEAR_ERROR',
    CLEAR_ALL_ERRORS: name + '/CLEAR_ALL_ERRORS',
};

interface ErrorState {
    errors: ErrorPayload[];
}

const initialState: ErrorState = {
    errors: [],
};

// function isErrorAlreadyExist(state: ErrorState, type: string): boolean {
//     return !!state.errors.find((err) => err.errorType === type);
// }

const reducer = (state = initialState, action) => {
    switch (action.type) {
        case types.DISPATCH_ERROR:
            return {
                ...state,
                errors: [...state.errors, { ...action.payload }],
            };

        case types.CLEAR_ERROR:
            return {
                ...state,
                errors: state.errors.filter((el) => el.errorType !== action.payload.errorType),
            };
        case types.CLEAR_ALL_ERRORS:
            return initialState;
        default:
            return state;
    }
};
export default reducer;

export const actions = {
    dispatchError: ({ errorType, message }) => ({
        type: types.DISPATCH_ERROR,
        payload: { errorType, message },
    }),
    clearError: (errorType) => ({
        type: types.CLEAR_ERROR,
        payload: { errorType },
    }),
    clearAllErrors: () => ({ type: types.CLEAR_ALL_ERRORS }),
};

function findError(state: ErrorPayload[], type: string | string[], filter: FilterProp) {
    const types = typeof type === 'string' ? [type] : type;
    // Apply the filter while searching: checked afterwards, an error stored first for another value
    // (e.g. another document card on the page) would hide the one matching this filter.
    return state && state.find((el) => types.includes(el.errorType) && (!filter || el[filter.key] === filter.value));
}

const localState = (state: any): ErrorState => state[name] || initialState;
/**
 * The types <ErrorMessage> shows through `editRiderError` below. Exported so a
 * page that clears before its own submit clears exactly what the banner can
 * display: only one of these is ever on screen at a time, since `findError`
 * returns the first match, so an entry left behind shadows the next failure.
 */
export const editRiderErrorTypes = [
    riderTypes.EDIT_RIDER,
    riderTypes.ANON_RIDER,
    riderTypes.PATCH_RIDER,
    riderTypes.SET_ADMIN_COMPANY,
    riderTypes.SAVE_RIDER_PROMO,
    riderTypes.CHANGE_RIDER_COMPANY,
    // LT-876: a group move can be refused (400 on a stale from_group_uuid, 409
    // when the re-join fails) and the user sheet fires one call per edited row,
    // so without this the failure reaches no ErrorMessage and the operator is
    // told nothing
    riderTypes.CHANGE_USER_GROUP,
    riderTypes.EDIT_USER,
];

export const selectors = {
    errors: (state) => localState(state).errors,
    getError: (state, errorType, filter?) => findError(selectors.errors(state), errorType, filter),
    loginError: (state) => selectors.getError(state, loginTypes.LOGIN),
    loginIDError: (state) => selectors.getError(state, loginTypes.LOGIN_ID),
    viaDialogError: (state) =>
        selectors.getError(state, [
            viaDialogTypes.GET_ALL_PHONES,
            viaDialogTypes.CONNECT_FREE_SEATING_USER,
            viaDialogTypes.DISCONNECT_FREE_SEATING_USER,
        ]),
    viaDialogCallError: (state) => selectors.getError(state, viaDialogTypes.CALL_PHONE),
    getPagesError: (state) => selectors.getError(state, tableTypes.GET_PAGES),
    getBillsExceptionnalError: (state) => selectors.getError(state, billTypes.PRODUCT_TYPES_BILLS_EXCEPTIONAL),
    saveExceptionalError: (state) => selectors.getError(state, billTypes.CREATE_BILLS_EXCEPTIONAL),
    saveRectificationError: (state) => selectors.getError(state, billTypes.CREATE_BILLS_RECTIFICATION),
    getBookingError: (state) => selectors.getError(state, bookingTypes.GET_BOOKING),
    getBookingOrCancelError: (state) =>
        selectors.getError(state, [bookingTypes.GET_BOOKING, bookingTypes.CANCEL_BOOKING]),
    saveBookingRefundError: (state) => selectors.getError(state, bookingTypes.SAVE_REFOUND),
    cancelBookingCallCenterError: (state) => selectors.getError(state, bookingTypes.CANCEL_BOOKING_CALL_CENTER),
    getIndustriesError: (state) => selectors.getError(state, companyTypes.GET_INDUSTRIES),
    getSaveCompaniesError: (state) => selectors.getError(state, companyTypes.SAVE_COMPANY),
    getCompaniesSaveContractError: (state) => selectors.getError(state, companyTypes.GET_COMPANY_CONTRACT),
    getSaveCompanyContractError: (state) => selectors.getError(state, companyTypes.SAVE_COMPANY_CONTRACT),
    getEditContractError: (state) => selectors.getError(state, companyTypes.EDIT_COMPANY_CONTRACT),
    getSaveDepartmentError: (state) => selectors.getError(state, companyTypes.SAVE_COMPANY_DEPARTMENT),
    getCompanyError: (state) => selectors.getError(state, companyTypes.GET_COMPANY),
    getContractError: (state) => selectors.getError(state, companyTypes.GET_CONTRACT),
    getEditCompanyError: (state) => selectors.getError(state, companyTypes.EDIT_COMPANY),
    getEditCompanyNewError: (state) => selectors.getError(state, companyTypes.EDIT_CONTRACT),
    getSaveMemberError: (state) => selectors.getError(state, companyTypes.SAVE_COMPANY_MEMBER),
    saveVehicleTypeError: (state) => selectors.getError(state, vehicleTypes.SAVE_VEHICLE_TYPE),
    getVehicleTypesError: (state) => selectors.getError(state, vehicleTypes.GET_VEHICLE_TYPES),
    getVehicleError: (state) => selectors.getError(state, vehicleTypes.GET_VEHICLE),
    editVehicleError: (state) => selectors.getError(state, vehicleTypes.EDIT_VEHICLE),
    setVehicleEnableError: (state) => selectors.getError(state, vehicleTypes.SET_ENABLE),
    getSnappayInfosError: (state) => selectors.getError(state, [snappayTypes.GET_STATS, snappayTypes.GET_TRANSACTIONS]),
    saveRiderPromoError: (state) => selectors.getError(state, riderTypes.SAVE_RIDER_PROMO),
    getRiderError: (state) => selectors.getError(state, riderTypes.GET_RIDER),
    getUserError: (state) => selectors.getError(state, riderTypes.GET_USER),
    editRiderError: (state) => selectors.getError(state, editRiderErrorTypes),
    getOrEditRiderError: (state) => selectors.getError(state, [riderTypes.GET_RIDER, riderTypes.EDIT_RIDER]),
    getOrEditUserError: (state) =>
        selectors.getError(state, [loginTypes.GET_USER, loginTypes.EDIT_USER, loginTypes.EDIT_PASSWORD]),
    // Reservations
    saveReservationError: (state) => selectors.getError(state, reservationTypes.CREATE_RESERVATION),
    deleteReservationRecurrentError: (state) =>
        selectors.getError(state, reservationTypes.DELETE_RESERVATION_RECURRENT),
    getReservationError: (state) => selectors.getError(state, reservationTypes.GET_RESERVATION),
    editReservationError: (state) => selectors.getError(state, reservationTypes.EDIT_RESERVATION),
    getOrEditReservationError: (state) =>
        selectors.getError(state, [reservationTypes.GET_RESERVATION, reservationTypes.EDIT_RESERVATION]),
    splitReservationError: (state) => selectors.getError(state, reservationTypes.SPLIT_RESERVATION),
    cancelReservationError: (state) => selectors.getError(state, reservationTypes.CANCEL_RESERVATION),
    cancelReservationCallCenterError: (state) =>
        selectors.getError(state, reservationTypes.CANCEL_RESERVATION_CALL_CENTER),

    // Rides
    getRideError: (state) => selectors.getError(state, rideTypes.GET_RIDE),

    // Pricing
    getCreateOrEditOrDeleteAreaError: (state) =>
        selectors.getError(state, [
            pricingTypes.GET_AREA,
            pricingTypes.GET_AREAS,
            pricingTypes.CREATE_AREA,
            pricingTypes.EDIT_AREA,
            pricingTypes.DELETE_AREA,
        ]),
    getRootAreaOrDeleteError: (state) =>
        selectors.getError(state, [
            pricingTypes.GET_ROOT_AREAS,
            pricingTypes.GET_ROOT_TREE_AREAS,
            pricingTypes.DELETE_AREA,
        ]),
    getOrDeleteAreaError: (state) => selectors.getError(state, [pricingTypes.GET_AREA, pricingTypes.DELETE_AREA]),
    getFeesAddError: (state) =>
        selectors.getError(state, [
            pricingTypes.GET_EXTRA,
            pricingTypes.CREATE_EXTRA,
            pricingTypes.EDIT_EXTRA,
            pricingTypes.GET_EXTRA_TYPES,
        ]),
    getFeesListError: (state) => selectors.getError(state, [pricingTypes.GET_EXTRAS, pricingTypes.DELETE_EXTRA]),
    getPricesAddError: (state) =>
        selectors.getError(state, [
            pricingTypes.GET_PRICING_SET,
            pricingTypes.GET_PRICING_SETS,
            pricingTypes.GET_SERVICES,
            pricingTypes.CREATE_PRICING_SET,
            pricingTypes.CREATE_GROUP_PRICING_SET,
            pricingTypes.EDIT_AREA,
        ]),
    getPricesAddTariffError: (state) =>
        selectors.getError(state, [
            pricingTypes.GET_TARIFF,
            pricingTypes.CREATE_TARIFF,
            pricingTypes.EDIT_TARIFF,
            pricingTypes.DELETE_TARIFF,
            pricingTypes.GET_AREAS,
        ]),
    getPricesListError: (state) =>
        selectors.getError(state, [pricingTypes.GET_PRICING_SETS, pricingTypes.DELETE_PRICING_SET]),
    getTariffsError: (state) => selectors.getError(state, pricingTypes.SEARCH_TARIFFS),
    getPricingSetError: (state) => selectors.getError(state, pricingTypes.GET_PRICING_SET),
    getComputePriceError: (state) => selectors.getError(state, pricingTypes.COMPUTE_TARIFF),

    updateCompanyPricingSetsError: (state) =>
        selectors.getError(state, [pricingTypes.CREATE_COMPANY_PRICING_SET, pricingTypes.DELETE_COMPANY_PRICING_SET]),
    updateGroupPricingSetsError: (state) =>
        selectors.getError(state, [pricingTypes.CREATE_GROUP_PRICING_SET, pricingTypes.DELETE_GROUP_PRICING_SET]),

    // Driver
    getDriverError: (state) => selectors.getError(state, driverTypes.GET_DRIVER),
    getDriverPointsError: (state) => selectors.getError(state, driverTypes.GET_DRIVER_POINTS),
    editDriverError: (state) => selectors.getError(state, driverTypes.EDIT_DRIVER),
    resetDriverPwdError: (state) => selectors.getError(state, driverTypes.RESET_DRIVER_PASSWORD),
    getControlTowersError: (state) => selectors.getError(state, driverTypes.GET_CONTROL_TOWERS),
    suspendDriverError: (state) => selectors.getError(state, driverTypes.SUSPEND_DRIVER),
    unsuspendDriverError: (state) => selectors.getError(state, driverTypes.UNSUSPEND_DRIVER),

    // Partner
    getPartnerAddError: (state) =>
        selectors.getError(state, [partnerTypes.SAVE_PARTNER, partnerTypes.GET_PAYMENT_RULES]),
    savePartnerDriverError: (state) => selectors.getError(state, [partnerTypes.SAVE_PARTNER_DRIVER]),
    getDispatchGroupsError: (state) => selectors.getError(state, [driverTypes.GET_DISPATCH_GROUPS]),
    editPartnerError: (state) => selectors.getError(state, [partnerTypes.GET_PARTNER, partnerTypes.EDIT_PARTNER]),
    savePartnerManagerError: (state) => selectors.getError(state, [partnerTypes.SAVE_PARTNER_MANAGER]),
    editPartnerManagerError: (state) =>
        selectors.getError(state, [partnerTypes.GET_PARTNER_MANAGER, partnerTypes.EDIT_PARTNER_MANAGER]),
    getPartnerManagerError: (state) => selectors.getError(state, [partnerTypes.GET_PARTNER_MANAGER]),
    saveAdjustmentError: (state) => selectors.getError(state, partnerTypes.SAVE_ADJUSTMENT),
    getVehicleModelsMotorizationAndColorsError: (state) =>
        selectors.getError(state, [
            partnerTypes.GET_VEHICLE_MODELS,
            partnerTypes.GET_VEHICLE_COLORS,
            partnerTypes.GET_VEHICLE_MOTORIZATIONS,
        ]),
    savePartnerVehicleError: (state) =>
        selectors.getError(state, [
            partnerTypes.SAVE_PARTNER_VEHICLE,
            partnerTypes.GET_VEHICLE_MODELS,
            partnerTypes.GET_VEHICLE_COLORS,
        ]),
    getPartnerError: (state) =>
        selectors.getError(state, [
            partnerTypes.GET_PARTNER,
            partnerTypes.GET_PARTNER_DRIVERS,
            partnerTypes.GET_PARTNER_MANAGERS,
            partnerTypes.GET_PARTNER_VEHICLES,
            partnerTypes.GET_PARTNER_BILLS_LOG,
            partnerTypes.GET_PAYMENT_RULES,
        ]),
    editPartnerBillsError: (state) =>
        selectors.getError(state, [partnerBillTypes.GET_PARTNER_BILL, partnerBillTypes.EDIT_PARTNER_BILL]),
    updatePartnerBillsPendingError: (state) =>
        selectors.getError(state, [
            partnerBillTypes.UPDATE_PARTNER_BILL_STATUS,
            partnerBillTypes.UPDATE_PARTNER_BILL_STATUSES,
        ]),
    getPartnerBillsTransactionsError: (state) =>
        selectors.getError(state, [
            partnerBillTypes.GET_PARTNER_TRANSACTIONS,
            partnerBillTypes.GET_PARTNER_TRANSACTIONS_ZIP,
        ]),
    bulkActionsPartnerBillsPendingError: (state) =>
        selectors.getError(state, [partnerBillTypes.BULK_VALIDATE, partnerBillTypes.BULK_SEND]),
    exportPaiementPartnerBillsPendingError: (state) => selectors.getError(state, [partnerBillTypes.EXPORT_PAIEMENT]),
    getPartnerBillsViewError: (state) =>
        selectors.getError(state, [partnerBillTypes.GET_PARTNER_BILL, partnerBillTypes.UPDATE_PARTNER_BILL_STATUS]),
    bulkActionsGroupedBillsPendingError: (state) =>
        selectors.getError(state, [billTypes.GROUPED_BILLS_PENDING_BULK_ACTION]),
    updateGroupedBillsPendingError: (state) =>
        selectors.getError(state, [billTypes.GROUPED_BILLS_PENDING_UPDATE_STATUS]),
    getGroupedStepError: (state) => selectors.getError(state, billTypes.GROUPED_BILLS_PENDING_STEP),
    setGroupedPaiementError: (state) => selectors.getError(state, billTypes.GROUPED_BILLS_PAIEMENT),
    saveCreditError: (state) => selectors.getError(state, billTypes.CREATE_BILLS_CREDIT),
    getDocumentError: (state, documentType) =>
        selectors.getError(
            state,
            [
                documentTypes.UPSERT_DOC,
                documentTypes.UPDATE_DOC_VERSION,
                documentTypes.SEND_SMS,
                documentTypes.GET_DOC_VERSION,
            ],
            { key: 'documentType', value: documentType },
        ),
    getGenerationPartnershipContractError: (state) =>
        selectors.getError(state, [
            partnerTypes.SAVE_PARTNERSHIP_CONTRACT,
            partnerTypes.REGENERATE_PARTNERSHIP_CONTRACT,
            documentTypes.ACTIVATE_PARTNER_EMAIL_CONTRACT,
        ]),
    findRider: (state) => selectors.getError(state, riderTypes.FIND_RIDER),
    findRiderWithPhone: (state) => selectors.getError(state, riderTypes.FIND_RIDERS_WITH_PHONE),
    findReservation: (state) => selectors.getError(state, reservationTypes.FIND_RESERVATION),
    getCreatePriceConfirmationError: (state) => selectors.getError(state, bookingTypes.CREATE_PRICE_CONFIRMATION),
    getConfirmPriceConfirmationError: (state) => selectors.getError(state, bookingTypes.CONFIRM_PRICE_CONFIRMATION),
    getRecalculateRefoundError: (state) => selectors.getError(state, bookingTypes.RECALCULATE_REFOUND),
    getIssueDetails: (state) =>
        selectors.getError(state, [
            ticketingTypes.GET_ISSUE,
            ticketingTypes.GET_ISSUE_CATEGORIES,
            ticketingTypes.GET_ISSUE_PRIORITIES,
            ticketingTypes.GET_ISSUE_STATUS,
        ]),
    upsertIssue: (state) =>
        selectors.getError(state, [
            ticketingTypes.CREATE_ISSUE,
            ticketingTypes.UPDATE_ISSUE,
            ticketingTypes.CREATE_FILES,
            ticketingTypes.DOWNLOAD_FILE,
        ]),
    crudPrebookError: (state) =>
        selectors.getError(state, [
            prebookTypes.GET_PREBOOK,
            prebookTypes.CREATE_PREBOOK,
            prebookTypes.UPDATE_PREBOOK,
            prebookTypes.DELETE_PREBOOK,
        ]),
    getPrebookGroupsError: (state) => selectors.getError(state, prebookTypes.GET_PREBOOKS),
    // Company
    getProgramError: (state) => selectors.getError(state, companyTypes.GET_FID_PROGRAM),
    createProgramError: (state) => selectors.getError(state, companyTypes.CREATE_FID_PROGRAM),
    deleteProgramError: (state) => selectors.getError(state, companyTypes.DELETE_FID_PROGRAM),
    getTravelPoliciesError: (state) => selectors.getError(state, companyTypes.GET_TRAVEL_POLICIES),
    createTravelPolicyError: (state) => selectors.getError(state, companyTypes.CREATE_TRAVEL_POLICY),
    getOrEditCompanyGroupPoliciesError: (state) =>
        selectors.getError(state, [companyTypes.GET_COMPANY_GROUP_POLICIES, companyTypes.EDIT_COMPANY_GROUP_POLICIES]),
    getOrEditContractGroupPoliciesError: (state) =>
        selectors.getError(state, [
            companyTypes.GET_CONTRACT_PAYMENT_SETTINGS,
            companyTypes.SET_GROUP_PAYMENT_SETTINGS,
            companyTypes.CREATE_PAYMENT_METHOD_SEPA,
            companyTypes.CREATE_PAYMENT_METHOD_AIRPLUS,
        ]),
    updateCompanyKAM: (state) => selectors.getError(state, [companyTypes.UPDATE_CONTRACT_KAM_TO_KAM]),
    // Admin
    getEmployeeByIDError: (state) => selectors.getError(state, adminTypes.GET_EMPLOYEE),
    getAllRolesError: (state) => selectors.getError(state, adminTypes.GET_ALL_ROLES),
    cudRoleToEmployeeError: (state) =>
        selectors.getError(state, [adminTypes.ADD_ROLE, adminTypes.MERGE_ROLE, adminTypes.REMOVE_ROLE]),
    // Marketing
    getPromoError: (state) => selectors.getError(state, marketingTypes.GET_PROMO),
    cudPromoError: (state) => selectors.getError(state, [marketingTypes.EDIT_PROMO, marketingTypes.SAVE_PROMO]),
    // Control Tower
    getControlTowerError: (state) => selectors.getError(state, [towerTypes.GET_TOWERS]),
    cudControlTowerError: (state) =>
        selectors.getError(state, [towerTypes.CREATE_TOWER, towerTypes.EDIT_TOWER, towerTypes.DELETE_TOWER]),
    // Ranks
    getRankError: (state) => selectors.getError(state, [rankTypes.GET_RANK, rankTypes.GET_RANKS]),
    cudRankError: (state) => selectors.getError(state, [rankTypes.CREATE_RANK]),
    // Driver communications
    getDriverCommunicationError: (state) =>
        selectors.getError(state, [
            driverCommunicationTypes.GET_DRIVER_COMMUNICATIONS,
            driverCommunicationTypes.GET_DRIVER_COMMUNICATION,
        ]),
    cudDriverCommunicationError: (state) =>
        selectors.getError(state, [
            driverCommunicationTypes.CREATE_DRIVER_COMMUNICATION,
            driverCommunicationTypes.UPDATE_DRIVER_COMMUNICATION,
            driverCommunicationTypes.DELETE_DRIVER_COMMUNICATION,
        ]),
    // Generic
    genericError: (state) =>
        selectors.getError(state, [genericTypes.GET_COMMENT, genericTypes.SAVE_COMMENT, genericTypes.DELETE_COMMENT]),
    // Actions fired from list or view pages (buttons, inline pickers, dialogs) rather than a dedicated form
    companyViewActionsError: (state) =>
        selectors.getError(state, [
            companyTypes.FORCE_BILLING,
            companyTypes.EDIT_COMPANY,
            companyTypes.EDIT_COMPANY_MEMBER,
            companyTypes.DELETE_COMPANY_CONTRACT,
            companyTypes.DELETE_COMPANY,
            companyTypes.EDIT_COMPANY_DISPATCH_GROUPS,
            pricingTypes.CREATE_COMPANY_EXTRA,
            pricingTypes.DELETE_COMPANY_EXTRA,
            pricingTypes.DELETE_COMPANY_PRICING_SET,
        ]),
    contractViewActionsError: (state) =>
        selectors.getError(state, [
            companyTypes.DELETE_COMPANY_CONTRACT,
            companyTypes.SET_CONTRACT_GROUPS_AUTOVALIDATE,
            // LT-881: id refuses an activation with a 409 and a reason — the
            // Corsair contract, the CORE sync off, no CORE client (LT-2321).
            // Listed here or the page's <ErrorMessage> never shows it and the
            // checkbox just springs back with no explanation.
            companyTypes.SET_CONTRACT_MULTIGROUP,
            pricingTypes.CREATE_COMPANY_EXTRA,
            pricingTypes.DELETE_COMPANY_EXTRA,
            pricingTypes.DELETE_COMPANY_PRICING_SET,
        ]),
    setPartnerPaymentRuleError: (state) => selectors.getError(state, partnerTypes.SET_PAYMENT_RULE),
    resetManagerPasswordError: (state) => selectors.getError(state, partnerTypes.RESET_MANAGER_PASSWORD),
    prebookDriversError: (state) =>
        selectors.getError(state, [prebookTypes.ADD_DRIVER_PREBOOK, prebookTypes.DELETE_DRIVER_PREBOOK]),
    pricingSetActionsError: (state) =>
        selectors.getError(state, [
            pricingTypes.IMPORT_PRICING_SET,
            pricingTypes.EXPORT_PRICING_SET,
            pricingTypes.DELETE_PRICING_SET,
            pricingTypes.DELETE_TARIFF,
        ]),
    simpleBillsActionsError: (state) =>
        selectors.getError(state, [
            billTypes.SIMPLE_BILLS_UNRECOVERABLE,
            billTypes.SIMPLE_BILLS_RECOVER,
            billTypes.SIMPLE_RETRY_PAIEMENT,
        ]),
    groupedBillsActionsError: (state) =>
        selectors.getError(state, [
            billTypes.GROUPED_BILLS_PAIEMENT,
            billTypes.SIMPLE_RETRY_PAIEMENT,
            billTypes.GROUPED_BILLS_EXPORT_ACCOUNTING,
            billTypes.GROUPED_BILLS_EXPORT_SEPA,
        ]),
    impersonateUserError: (state) => selectors.getError(state, riderTypes.IMPERSONATE_USER),
    importRideError: (state) => selectors.getError(state, ridesImportTypes.IMPORT_RIDE),
    resetPasswordError: (state) => selectors.getError(state, loginTypes.RESET_PASSWORD),
};
